import { createCollectionStore } from "../../../shared/data/createCollectionStore.js";
import {
  getInvoices,
  getInvoiceById,
  createInvoiceWithStockDeduction,
  deleteInvoiceWithStockRestoration
} from "../services/invoiceService.js";
import { recordCustomerPaymentTally } from "../services/paymentService.js";
import { calculateFifoAllocations } from "../utils/paymentCalculations.js";

// One shared, cached copy of all invoices for every screen (loaded once per session)
export const invoiceStore = createCollectionStore(getInvoices, {
  errorMessage: "Unable to load invoices. Please check your database connection."
});

export function useInvoices() {
  const { data: invoices, loading, error } = invoiceStore.useStore();
  const fetchInvoices = invoiceStore.refresh;

  const addInvoice = async (invoicePayload) => {
    const id = await createInvoiceWithStockDeduction(invoicePayload);
    await fetchInvoices();
    return id;
  };

  // Single-invoice payment, saved as a proper receipt so it can be edited or voided later
  const recordPayment = async (invoiceId, { paidAmount, paymentMode = "Cash", paymentDate, reference = "" } = {}) => {
    const invoice = invoices.find((inv) => inv.id === invoiceId) || await getInvoiceById(invoiceId);
    if (!invoice) throw new Error("Invoice not found.");
    const amount = Number(paidAmount || 0);
    const { allocations, totalApplied, unallocatedAmount } = calculateFifoAllocations([invoice], amount);

    const res = await recordCustomerPaymentTally({
      customerId: invoice.customerId,
      customerName: invoice.customerName,
      customerPhone: invoice.customerPhone || "",
      companyId: invoice.companyId || null,
      amount,
      totalApplied,
      unallocatedAmount,
      paymentDate: paymentDate || new Date().toISOString().split("T")[0],
      paymentMode,
      reference,
      allocations
    });
    await fetchInvoices();
    return res;
  };

  const removeInvoice = async (invoiceId) => {
    const res = await deleteInvoiceWithStockRestoration(invoiceId);
    await fetchInvoices();
    return res;
  };

  return {
    invoices,
    loading,
    error,
    refetch: fetchInvoices,
    addInvoice,
    recordPayment,
    removeInvoice,
    fetchInvoiceById: getInvoiceById
  };
}

export default useInvoices;
